import React from 'react'

import { useTranslation } from 'react-i18next'

import { Badge } from '@cmsgov/design-system'

import { GovBanner } from '../GovBanner/GovBanner'

import { NavMenu } from '../NavMenu'
import MedicareLogo from '../MedicareLogo/MedicareLogo'
import { MobileNavigation } from '../MobileNavigation/MobileNavigation'

import User from '../../models/User'
import Message from '../../models/Message'

import { getResourceNavItems, getAccountNav } from '../../data/navigation'

import { MessageCenterDropdown } from '../MessageCenterDropdown/MessageCenterDropdown'
import { ResourceNavDropdown } from '../ResourceNavDropdown/ResourceNavDropdown'
import { LiveChatMenu } from '../LiveChatMenu/LiveChatMenu'
import { DropdownMenu } from '../DropdownMenu/DropdownMenu'

import envIcon from '../../images/Icon_Envelope.svg'
import userIcon from '../../images/Icon_User.svg'
import logIn from '../../images/Log_In.svg'
import logOut from '../../images/Log_Out.svg'
import chatIcon from '../../images/Icon_Chat.svg'

import { properCase } from '../../helper/string'
import { accountUrls, messageUrls } from '../../data/urls'
import getSubdomain from '../../utils/getSubdomain'
import { sendTealiumClickEvent, ClickEventOptions } from '../../utils/sendTealiumEvent'
import classNames from 'classnames'

import { CATEGORIES } from '../../constants/analytics.json'

const WITH_MESSAGE_CENTER = false
const legacyMessageCenterUrl = `https://${getSubdomain(process.env.REACT_APP_MY_MEDICARE_ENV || '')}mymedicare.gov/mymessages.aspx`
const medicareEnv = process.env.REACT_APP_MEDICARE_ENV || ''

export interface GlobalHeaderProps {
  loginUrl: string;
  logoutUrl: string;
  onViewMoreMessagesClick?: () => void;
  user?: User;
  messages?: Message[];
  languageToggleLink: string;
  enableLiveChat?: boolean;
}

export const GlobalHeader: React.FC<GlobalHeaderProps> = ({ loginUrl, logoutUrl, onViewMoreMessagesClick, user, messages = [], languageToggleLink, enableLiveChat = false }) => {
  const { t, i18n } = useTranslation('common')
  const lang = (i18n.language === 'es') ? 'es' : 'en'

  const ignoredSubdomain = lang === 'es' ? 'www' : undefined
  const medicareSubdomain = getSubdomain(medicareEnv, ignoredSubdomain)
  const langSubdomain = getSubdomain(lang, 'en')

  const medicareUrl = `https://${langSubdomain + medicareSubdomain}medicare.gov`
  // TODO - put getResourceNavItems in a useMemo hook as it gets executed 4 times on pageload. Same for getAccountNav
  const resourceNav = getResourceNavItems(lang)
  const accountNav = getAccountNav(lang)
  const firstName = user ? properCase(user.firstName) : ''

  const unreadMessages = messages.filter(message => message.read === false)
  const messageBadge = unreadMessages.length && <Badge variation='warn'>{unreadMessages.length}</Badge>

  const resourceNavWithDropdowns = resourceNav.map(section => {
    const filteredSubsections = section.subsections.filter(subsection => !(user && subsection.unauthedOnly))
    return {
      ...section,
      hasAccordionCaret: true,
      dropdown: (
        <ResourceNavDropdown name={section.label} sections={filteredSubsections} isLoggedIn={!!user} analyticsCategory={CATEGORIES.GENERAL_NAV} />
      )
    }
  })

  const messageCenterButton = WITH_MESSAGE_CENTER
    ? { locationUrls: messageUrls, icon: envIcon, label: t('Message Center'), badge: messageBadge, dropdown: (<MessageCenterDropdown messages={messages} onViewMoreMessagesClick={onViewMoreMessagesClick} />) }
    : { locationUrls: messageUrls, icon: envIcon, label: t('Message Center'), href: legacyMessageCenterUrl, target: '_blank' }

  const userMenu = []
  if (user) {
    userMenu.push({
      locationUrls: accountUrls,
      gaText: 'Account',
      icon: userIcon,
      label: firstName,
      dropdown: (
        <ResourceNavDropdown isLoggedIn name='Account' analyticsCategory={CATEGORIES.PERSONAL_NAV} sections={accountNav} />
      )
    })
    userMenu.push(messageCenterButton)
  }

  if (enableLiveChat) {
    userMenu.push(
      {
        label: t('Chat'),
        icon: chatIcon,
        dropdown: <DropdownMenu><LiveChatMenu isAuthenticated={!!user} openChatAsPopup /></DropdownMenu>
      }
    )
  }

  if (user) {
    userMenu.push({ label: t('Log out'), icon: logOut, href: logoutUrl })
  } else {
    userMenu.push({ label: t('Log in'), icon: logIn, href: loginUrl })
  }

  // TODO: Update the standard nav to use the same navLists format as the MobileNavigation
  const mobileResourceNavItems = resourceNav.map(link => {
    const subsections = link.subsections.filter((section: any) => !user || !section.unauthedOnly)
    return { ...link, subsections, analyticsCategory: CATEGORIES.GENERAL_NAV }
  })

  // id is only used to pass as a key when mapping over the links
  const mobileAccountNavItems = [
    {
      id: 'mobile-account-nav-1',
      analyticsCategory: CATEGORIES.PERSONAL_NAV,
      gaText: 'Account',
      label: firstName,
      subsections: accountNav,
      authOnly: true,
      icon: userIcon,
      enabled: true
    },
    {
      id: 'mobile-account-nav-2',
      analyticsCategory: CATEGORIES.PERSONAL_NAV,
      label: t('Chat'),
      menu: <LiveChatMenu isAuthenticated={!!user} openChatAsPopup={false} />,
      authOnly: false,
      icon: chatIcon,
      enabled: enableLiveChat
    },
    {
      id: 'mobile-account-nav-3',
      analyticsCategory: CATEGORIES.PERSONAL_NAV,
      label: t('Message Center'),
      href: legacyMessageCenterUrl,
      authOnly: true,
      icon: envIcon,
      enabled: true
    }
  ].filter(menuItem => (!menuItem.authOnly || !!user) && menuItem.enabled).map(({ authOnly, ...menuItem }) => menuItem)

  const medicareLogoClickEventOptions: ClickEventOptions = {
    text: 'Medicare.gov',
    logged_in: !!user,
    target_location: medicareUrl,
    category: CATEGORIES.LOGO
  }

  const mainWrapperClass = classNames(`
  ds-u-display--flex ds-u-align-items--center ch-c-header`,
  { 'language-es': i18n.language === 'es' },
  { 'language-en': i18n.language === 'en' })

  return (
    <header>
      <GovBanner isLoggedIn={!!user} languageToggleLink={languageToggleLink} />
      <div className={mainWrapperClass}>
        <a
          className='ds-u-margin-right--auto nav-menu-button ch-c-medicare-logo-wrapper'
          href={medicareUrl}
          onClick={() => sendTealiumClickEvent(medicareLogoClickEventOptions)}
        >
          <MedicareLogo />
        </a>
        <NavMenu className='ch-u-remove--md' items={resourceNavWithDropdowns} ariaLabel='Main' analyticsCategory={CATEGORIES.GENERAL_NAV} isLoggedIn={!!user} />
        <NavMenu className='ch-u-remove--md' items={userMenu} ariaLabel='Personal' analyticsCategory={CATEGORIES.PERSONAL_NAV} isLoggedIn={!!user} />
        <MobileNavigation className='ch-u-remove--lg' navLists={[mobileResourceNavItems, mobileAccountNavItems]} isLoggedIn={!!user} languageToggleLink={languageToggleLink} loginUrl={loginUrl} logoutUrl={logoutUrl} />
      </div>
    </header>
  )
}
